import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, onUnauthenticated } from "./api";
import type { CurrentUser } from "./types";

interface AuthState {
  user: CurrentUser | null;
  /** True until the first `/auth/me` settles, so no screen renders too early. */
  loading: boolean;
  setUser: (user: CurrentUser | null) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Resolved before the router renders anything. Without it a reload shows the
  // login screen for a moment and then replaces it, which reads as being
  // logged out and is alarming when it happens mid-task.
  useEffect(() => {
    let cancelled = false;

    api<{ user: CurrentUser }>("/api/v1/auth/me")
      .then((result) => {
        if (!cancelled) setUser(result.user);
      })
      .catch((error: unknown) => {
        // 401 here is the normal "not signed in yet" answer, not a failure.
        if (!cancelled && !(error instanceof ApiError && error.isAuth)) {
          console.error(error);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // A session can lapse between requests. Clearing the cache matters as much as
  // clearing the user: without it the next sign-in briefly shows whatever the
  // previous account had loaded.
  useEffect(
    () =>
      onUnauthenticated(() => {
        setUser(null);
        queryClient.clear();
      }),
    [queryClient],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      setUser,
      signOut: async () => {
        try {
          await api<void>("/api/v1/auth/logout", { method: "POST" });
        } finally {
          setUser(null);
          queryClient.clear();
        }
      },
    }),
    [user, loading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

export function useCurrentUser(): CurrentUser {
  const { user } = useAuth();
  if (user === null) throw new Error("useCurrentUser must be used inside a protected route");
  return user;
}
